import React from 'react';
import logger from '../../utils/logger';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      logger.error('App', 'ErrorBoundary caught an error:', error, errorInfo);
    }
    
    // Track error for debugging
    try {
      // Log to analytics if available
      if (window.gtag) {
        window.gtag('event', 'exception', {
          description: `ErrorBoundary: ${error.toString()} in ${this.props.pageName || 'unknown page'}`,
          fatal: false,
          error_stack: errorInfo.componentStack
        });
      }
      
      // Log structured error details
      logger.error('ErrorBoundary', 'Error', {
        pageName: this.props.pageName,
        errorMessage: error.toString(),
        errorStack: error.stack,
        componentStack: errorInfo.componentStack,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      });
    } catch (trackingError) {
      logger.error('ErrorBoundary', 'Error', trackingError);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div 
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          className="bg-red-50 border border-red-200 rounded-lg p-4 m-4"
        >
          <div className="flex items-center">
            <div className="text-red-400 text-2xl mr-3" aria-hidden="true">{'\u26A0\uFE0F'}</div>
            <div>
              <h3 className="text-red-800 font-semibold" id="error-title">
                Something went wrong
              </h3>
              <p className="text-red-600 text-sm mt-1" id="error-description">
                {this.props.fallbackMessage || 'An error occurred while loading this content.'}
              </p>
              {this.props.showRetry && (
                <button
                  onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
                  className="mt-2 px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                  aria-label="Try loading this content again"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;